/**
 * Accessibility Remediation Script
 * =================================
 * Fixes accessibility issues that originate from:
 *   1. Third-party app iframes (e.g. Tydal Reviews) missing titles
 *   2. Product description HTML (authored in Shopify admin) containing:
 *      - Links with images that have empty alt="" attributes
 *      - Empty links with no text content
 *
 * These cannot be fixed in Liquid templates because the content is either
 * injected by apps at runtime or stored in product body_html.
 * Only activates as a fallback — if content already has proper accessible
 * names, this script does nothing.
 *
 * Rules addressed:
 *   - frame-title (WCAG 2.2 AA / 4.1.2)
 *   - link-name  (WCAG 2.2 AA / 2.4.4, 4.1.2)
 */
(() => {
  'use strict';

  /** Map of URL patterns to human-readable aria-labels */
  const LINK_LABEL_MAP = [
    { pattern: 'instagram.com', label: 'Follow the author on Instagram' },
    { pattern: 'facebook.com', label: 'Follow the author on Facebook' },
    { pattern: 'twitter.com', label: 'Follow the author on X (Twitter)' },
    { pattern: 'x.com', label: 'Follow the author on X' },
    { pattern: 'linkedin.com', label: 'Follow the author on LinkedIn' },
    { pattern: 'readingreligion.org', label: 'Read the review on Reading Religion' },
    { pattern: 'pres-outlook.org', label: 'Read the review on Presbyterian Outlook' },
    { pattern: 'youtube.com', label: 'Watch on YouTube' },
    { pattern: '/products/', label: 'View related product' },
    { pattern: 'shopifypreview.com', label: 'View related product' },
  ];

  /**
   * Derive an accessible label for a link based on its href.
   * @param {string} href
   * @returns {string}
   */
  const getLabelForHref = (href) => {
    const lower = href.toLowerCase();
    const match = LINK_LABEL_MAP.find(({ pattern }) => lower.includes(pattern));
    if (match) return match.label;

    try {
      const domain = new URL(href).hostname.replace('www.', '');
      return `Visit ${domain}`;
    } catch {
      return 'External link';
    }
  };

  /**
   * 1. FRAME-TITLE: Add accessible names to app iframes
   */
  const fixIframes = () => {
    const tydalIframe = document.getElementById('tydal-reviews-panel');
    if (tydalIframe && !tydalIframe.title) {
      tydalIframe.title = 'Customer Reviews';
    }

    // Generic: any iframe without a title or aria-label
    document
      .querySelectorAll('iframe:not([title]):not([role="presentation"]):not([role="none"])')
      .forEach((iframe) => {
        if (iframe.getAttribute('aria-label')) return;

        const name = (iframe.id || iframe.name || '').toLowerCase();
        if (name.includes('review')) {
          iframe.title = 'Customer Reviews';
        } else if (name.includes('share') || name.includes('social')) {
          iframe.title = 'Social Sharing';
        } else {
          iframe.title = 'Embedded Content';
        }
      });
  };

  /**
   * 2. LINK-NAME: Add aria-labels to links without accessible text
   */
  const fixEmptyLinks = () => {
    const descContainer = document.querySelector('.product__description');
    if (!descContainer) return;

    descContainer.querySelectorAll('a').forEach((link) => {
      const hasText = link.textContent?.trim();
      const hasAriaLabel = link.getAttribute('aria-label');
      const hasTitle = link.getAttribute('title');
      const img = link.querySelector('img');
      const imgHasAlt = img?.getAttribute('alt')?.trim();

      // Skip links that already have accessible names
      if (hasText || hasAriaLabel || hasTitle || imgHasAlt) return;

      const label = getLabelForHref(link.href || '');
      link.setAttribute('aria-label', label);

      // Also fix empty alt on the image inside the link
      if (img) {
        img.setAttribute('alt', label);
      }
    });
  };

  /** Run all remediations */
  const remediate = () => {
    fixIframes();
    fixEmptyLinks();
  };

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', remediate);
  } else {
    remediate();
  }

  // Re-run after a delay to catch app-injected content (e.g. Tydal)
  setTimeout(remediate, 2000);
})();
